import { type Product, type ProductAPI } from '@/features/shop/services/Product/types'
import {
  normalizeProduct,
  getProductType,
  PRODUCT_TYPES,
} from '@/features/shared/utils/normalizeProduct'
import {
  type Cart,
  type CartLookups,
  type CartLine,
  type CartResponse,
} from '@/features/shop/services/Cart/types'
import { normalizeCampaignsResponse } from '@/features/shop/services/Campaigns/utils'
export { getCartConversionRoute } from '@/features/shop/services/Cart/utils/getCartConversionRoute'

// Minimal type definitions for utility functions
type RoundedQuantityProduct = {
  type?: string
  hasCustomLabel?: boolean
}

type QuantityForProductTypeProduct = {
  quantity: number
  type: string
  hasCustomLabel: boolean
  unitWeight: number
}

type OptimisticCartQuantityProduct = {
  id: number
  quantity: number
  type: string
  hasCustomLabel: boolean
}

type WeightedProductLabelProduct = {
  type: string
  hasCustomLabel: boolean
  unitWeight: number
  descriptionLabels?: string[]
}

// the fields needed to key a product into the cart lookups
type CartLookupProduct = Pick<
  Product,
  'id' | 'note' | 'quantity' | 'isCustomProduct' | 'parentProductId'
>

export type CartProductsForNewOrderProduct = {
  id: number
  name: string
  note: string
  isCustomProduct: boolean
  couponId: string
  onSale: boolean
  totalSalePrice: number
  totalPrice: number
  universal_id: string
  quantity: number
  type: string
  hasCustomLabel: boolean
  unitWeight: number
}

// For UI purposes (i.e. in the ProductQuantitySelector) we want to round the
// quantities that are saved within a custom input to reflect an accurate quantity.
// For example, we want to round custom quantities for 'by weight' products
// that don't have a custom label to the nearest 0.5 increment. For all other
// products, we just want to round to the nearest whole number.
export const roundedQuantity = (product?: RoundedQuantityProduct, quantity = 1) => {
  if (product?.type === 'by weight' && !product.hasCustomLabel) {
    return Math.round(quantity * 2) / 2
  } else {
    return Math.round(quantity)
  }
}

// Some products are sold by weight (e.g. packaged steaks, deli meats, apples,
// etc.); some of those "by weight" products also have a custom label override.
// If a product is "by weight", we want to use the Shipt-defined "unit weight"
// of that product to represent the product's singular quantity; for "normal"
// products (i.e. products not "by weight"), the singular quantity is just 1.
//
// Relevant (may need to request access): http://bit.ly/2EUDiWK
export const quantityForProductType = (product: QuantityForProductTypeProduct) => {
  const quantity = product.quantity
  if (product.type === 'by weight') {
    if (product.hasCustomLabel) {
      return product.unitWeight * quantity
    } else {
      // We want to round custom quantities for 'by weight' products that
      // don't have a custom label to the nearest 0.5 increment
      return Math.round(quantity * 2) / 2
    }
  } else {
    return quantity
  }
}

export const getOptimisticCartQuantity = (
  cartProducts: OptimisticCartQuantityProduct[],
  products: OptimisticCartQuantityProduct[],
) => {
  const productsQuantity = products.reduce<number>((totalQuantity, product) => {
    const quantity = getProductType(product) === PRODUCT_TYPES.BY_WEIGHT ? 1 : product.quantity
    return totalQuantity + quantity
  }, 0)

  const productsIds = products.map((product) => product.id)

  return cartProducts.reduce<number>((totalQuantity, cartProduct) => {
    if (!productsIds.includes(cartProduct.id)) {
      // Shipt has decided that some "by weight" products, those *without* a
      // custom label override, are sold in half-unit increments, effectively
      // making the "unit weight" of this type of product "0.5".
      //
      // Each time a user adds one of these products to their cart, we want to
      // show the actual half-unit increment in the product grid card, but count
      // the item as 1 in the cart. For example, if a user clicks "add" 5 times on
      // this type of product in the grid view, the product card should read
      // "2.5 lbs" while the total cart quantity should have increased by 1.
      if (getProductType(cartProduct) === PRODUCT_TYPES.BY_WEIGHT) {
        return totalQuantity + 1
      }
      return totalQuantity + cartProduct.quantity
    }
    return totalQuantity
  }, productsQuantity)
}

export const flattenAndNormalizeProduct = (cartProduct: CartLine) => {
  const productAPI: ProductAPI = {
    product_id: cartProduct.product_id,
    note: cartProduct.note,
    coupon_id: cartProduct.coupon_id,
    quantity: cartProduct.qty,
    promotion_subtotal: cartProduct.promotion_subtotal,
    original_subtotal: cartProduct.original_subtotal,
    promotion_metadata: cartProduct.promotion_metadata,
    product_type: cartProduct.product_type,
    is_custom_product: cartProduct.product_type === 'CustomProduct',
    ...cartProduct.product,
  }
  return normalizeProduct(productAPI)
}

export const getWeightedProductLabel = (product: WeightedProductLabelProduct) => {
  if (getProductType(product) === PRODUCT_TYPES.CUSTOM_BY_WEIGHT) {
    const { unitWeight, descriptionLabels = [] } = product

    return `About ${unitWeight} lb per ${descriptionLabels[0]}`
  }

  return undefined
}

export const cartProductsForNewOrder = (shoppingCartLines: CartProductsForNewOrderProduct[]) => {
  return shoppingCartLines.map((product) => {
    const {
      id,
      name,
      note,
      isCustomProduct,
      couponId,
      onSale,
      totalSalePrice,
      totalPrice,
      universal_id,
    } = product
    const requestedQuantity = quantityForProductType(product)
    const orderLine = {
      universal_id,
      requested_product_id: id,
      requested_qty: requestedQuantity,
      notes: note,
      price: onSale ? totalSalePrice : totalPrice,
      original_price: totalPrice,
      requested_product_type: isCustomProduct ? 'CustomProduct' : 'Product',
      ...(couponId && { coupon_id: couponId }),
    }

    // API source code: https://goo.gl/s8hMoN
    if (isCustomProduct) {
      return {
        ...orderLine,
        requested_product_attributes: {
          description: name,
        },
      }
    }

    return orderLine
  })
}

// kept as plain records rather than Maps so the cart cache survives SSR JSON serialization
export const createEmptyCartLookups = (): CartLookups => ({
  noteById: {},
  quantityById: {},
  parentQuantityById: {},
})

export const createDefaultCartQueryState = (): Cart => ({
  num_items: 0,
  savings_applied_subtotal: 0,
  pre_savings_subtotal: 0,
  requested_subtotal_with_fees: 0,
  shopping_cart_lines: [],
  shopping_cart_lines_oos: [],
  fees: [],
  total_savings: 0,
  is_alcohol: false,
  id: '',
  promotions: {
    availablePromotions: [],
    dollarTotalQualifyingCredit: 0,
  },
  ...createEmptyCartLookups(),
})

export const applyProductsToCartLookups = (
  base: CartLookups,
  products: CartLookupProduct[],
): CartLookups => {
  const noteById = { ...base.noteById }
  const quantityById = { ...base.quantityById }
  const parentQuantityById = { ...base.parentQuantityById }

  for (const product of products) {
    // skip custom products
    if (product.isCustomProduct) continue

    const { id, note, quantity, parentProductId } = product

    // if product has a parentProductId, calculate all the quantity for that parent
    if (parentProductId) {
      const delta = quantity - (quantityById[id] ?? 0)
      const parentQuantity = (parentQuantityById[parentProductId] ?? 0) + delta

      if (parentQuantity > 0) {
        parentQuantityById[parentProductId] = parentQuantity
      } else {
        delete parentQuantityById[parentProductId]
      }
    }

    // always set individual product quantity and note
    if (quantity === 0) {
      delete quantityById[id]
      delete noteById[id]
    } else {
      quantityById[id] = quantity
      noteById[id] = note
    }
  }

  return { noteById, quantityById, parentQuantityById }
}

export const buildCartLookups = (products: CartLookupProduct[]): CartLookups => {
  return applyProductsToCartLookups(createEmptyCartLookups(), products)
}

export const normalizeCartResponse = (cartResponse: CartResponse): Cart => {
  const shoppingCartLines = cartResponse.shopping_cart_lines.map(flattenAndNormalizeProduct)
  return {
    ...cartResponse,
    shopping_cart_lines: shoppingCartLines,
    shopping_cart_lines_oos: cartResponse.shopping_cart_lines_oos.map(flattenAndNormalizeProduct),
    promotions: normalizeCampaignsResponse(cartResponse.promotions || []),
    ...buildCartLookups(shoppingCartLines),
  }
}
